/**
 * Pure helpers for the WagePro-branded ESS invite-accept screen (WagePro
 * signup epic, `invite_accept_screen.tsx`). No React, no I/O — unit-tested
 * in isolation, matching this codebase's existing `auth_gate_screen.ts`/
 * `apps/tax-web/src/screens/ess_accept_logic.ts` "pure logic" convention.
 *
 * Calls `essInviteSignup.lookup` / `essInviteSignup.acceptWithCredentials`
 * (`apps/api/src/trpc/routers/ess_invite_signup.ts`).
 */

import { passwordConfirmationMatches } from './password_confirmation.js';

/** The pre-auth pathname `main.tsx` gates on — the link `payroll.createEssInvite` mints. */
export const INVITE_ACCEPT_PATH = '/invite/accept';

export interface InviteAcceptParams {
  token: string;
  entity_id: string;
}

/**
 * Parse `token` + `entity_id` from a query string (`window.location.search`).
 * Both are required; either missing or empty resolves to `null` so the
 * screen can show one generic "invalid link" state rather than partially
 * processing a malformed link — same contract as `apps/tax-web/src/screens/
 * ess_accept_logic.ts`'s `parseEssAcceptParams`.
 */
export function parseInviteAcceptParams(
  search: string,
): InviteAcceptParams | null {
  const params = new URLSearchParams(search);
  const token = params.get('token');
  const entity_id = params.get('entity_id');
  if (!token || !entity_id) return null;
  return { token, entity_id };
}

/** Which form the accept screen renders, driven by `essInviteSignup.lookup`'s `account_exists`. */
export type InviteAcceptMode = 'sign_in' | 'create_account';

/**
 * `essInviteSignup.lookup` returns `account_exists: true` when an account
 * already holds the invited email — in that case the screen only needs a
 * password field ("sign in and accept"); otherwise it needs the full signup
 * fields ("create account and accept"). Extracted to its own named function
 * (rather than an inline ternary at the call site) so the branch is a single
 * pinned assertion, matching `auth_gate_screen.ts`'s `resolveAuthGateScreen`
 * convention for screen-selection logic.
 */
export function resolveInviteAcceptMode(
  accountExists: boolean,
): InviteAcceptMode {
  return accountExists ? 'sign_in' : 'create_account';
}

export const MIN_PASSWORD_LENGTH = 12;

export interface InviteAcceptSignInValues {
  password: string;
}

export interface InviteAcceptCreateAccountValues {
  password: string;
  /**
   * Must equal `password` exactly — no trimming, no case folding. Client-side
   * only: never sent to the API.
   */
  confirm_password: string;
  full_name: string;
  accept_tos: boolean;
  accept_privacy: boolean;
}

export type InviteAcceptFieldErrors = Partial<
  Record<
    | 'password'
    | 'confirm_password'
    | 'full_name'
    | 'accept_tos'
    | 'accept_privacy',
    true
  >
>;

/** Validates the "sign in and accept" branch — password only. */
export function validateSignInValues(
  values: InviteAcceptSignInValues,
): InviteAcceptFieldErrors {
  const errors: InviteAcceptFieldErrors = {};
  if (values.password.length < MIN_PASSWORD_LENGTH) errors.password = true;
  return errors;
}

/**
 * Validates the "create account and accept" branch, mirroring
 * `essInviteSignupAcceptWithCredentialsProcedure`'s new-account branch
 * requirement (`full_name`/`accept_tos`/`accept_privacy` all required —
 * `ess_invite_signup.ts`).
 */
export function validateCreateAccountValues(
  values: InviteAcceptCreateAccountValues,
): InviteAcceptFieldErrors {
  const errors: InviteAcceptFieldErrors = {};
  if (values.password.length < MIN_PASSWORD_LENGTH) errors.password = true;
  if (!passwordConfirmationMatches(values.password, values.confirm_password)) {
    errors.confirm_password = true;
  }
  if (values.full_name.trim().length === 0) errors.full_name = true;
  if (!values.accept_tos) errors.accept_tos = true;
  if (!values.accept_privacy) errors.accept_privacy = true;
  return errors;
}

export function hasInviteAcceptFieldErrors(
  errors: InviteAcceptFieldErrors,
): boolean {
  return Object.keys(errors).length > 0;
}

// ── One-time invite code channel (`invite_code_screen.tsx`) ────────────────

/**
 * The pre-auth pathname for "First time signing in?" — the employee types the
 * email + one-time code from their invitation email (the WagePro-app channel;
 * `essInviteSignup.lookupByCode` / `acceptWithCode`).
 */
export const INVITE_CODE_PATH = '/invite/code';

/** Significant characters in a code (shown to the employee as `XXXXX-XXXXX`). */
export const INVITE_CODE_LENGTH = 10;

const CANONICAL_CODE_RE = /^[0-9A-HJKMNP-TV-Z]{10}$/;

/**
 * Canonicalise a typed code exactly as the server does
 * (`apps/api/src/services/payroll/ess_invite_code.ts`): uppercase, drop
 * whitespace and hyphens, fold Crockford's confusables (O→0, I/L→1).
 */
export function normalizeInviteCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[\s-]+/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
}

/** True when `raw` normalises to exactly 10 Crockford base32 characters. */
export function isCompleteInviteCode(raw: string): boolean {
  return CANONICAL_CODE_RE.test(normalizeInviteCode(raw));
}

export interface InviteCodeFormValues {
  email: string;
  code: string;
}

export type InviteCodeFieldErrors = Partial<Record<'email' | 'code', true>>;

/**
 * Light client-side pre-check for the code step. The server re-validates
 * everything; this only stops an obviously incomplete submit (which would
 * otherwise spend one of the address's five lockout attempts).
 */
export function validateInviteCodeForm(
  values: InviteCodeFormValues,
): InviteCodeFieldErrors {
  const errors: InviteCodeFieldErrors = {};
  const email = values.email.trim();
  if (email.length === 0 || !email.includes('@')) errors.email = true;
  if (!isCompleteInviteCode(values.code)) errors.code = true;
  return errors;
}

export function hasInviteCodeFieldErrors(
  errors: InviteCodeFieldErrors,
): boolean {
  return Object.keys(errors).length > 0;
}

/**
 * What proves the right to accept, per channel. Held in React state only —
 * never written to any storage (`scripts/lint-token-storage.sh`).
 */
export type InviteCredential =
  | { kind: 'link'; entity_id: string; token: string }
  | { kind: 'code'; email: string; code: string };

/** The set-password form's own fields. */
export interface InviteSetPasswordValues {
  password: string;
  full_name: string;
  accept_tos: boolean;
  accept_privacy: boolean;
}

/** Which accept mutation to call, with exactly the fields it takes. */
export type InviteAcceptRequest =
  | {
      procedure: 'acceptWithCredentials';
      input: {
        entity_id: string;
        token: string;
        email: string;
        password: string;
        full_name?: string;
        accept_tos?: boolean;
        accept_privacy?: boolean;
        client?: 'native-shell';
      };
    }
  | {
      procedure: 'acceptWithCode';
      input: {
        email: string;
        code: string;
        password: string;
        full_name?: string;
        accept_tos?: boolean;
        accept_privacy?: boolean;
        client?: 'native-shell';
      };
    };

/**
 * Build the accept request for either channel. Consent fields are sent only
 * on the create-account branch; `client` is spread in by the caller from
 * `nativeClientInput(isNative())`.
 */
export function buildInviteAcceptRequest(params: {
  credential: InviteCredential;
  invitedEmail: string;
  mode: InviteAcceptMode;
  values: InviteSetPasswordValues;
  client: { client?: 'native-shell' };
}): InviteAcceptRequest {
  const account =
    params.mode === 'create_account'
      ? {
          full_name: params.values.full_name.trim(),
          accept_tos: true,
          accept_privacy: true,
        }
      : {};
  if (params.credential.kind === 'link') {
    return {
      procedure: 'acceptWithCredentials',
      input: {
        entity_id: params.credential.entity_id,
        token: params.credential.token,
        email: params.invitedEmail,
        password: params.values.password,
        ...account,
        ...params.client,
      },
    };
  }
  return {
    procedure: 'acceptWithCode',
    input: {
      email: params.credential.email,
      code: params.credential.code,
      password: params.values.password,
      ...account,
      ...params.client,
    },
  };
}

// A caught `essInviteSignup.{lookup,acceptWithCredentials}` rejection is
// turned into display copy by `classifyAuthError`
// (`lib/auth/auth_error_display.ts`), shared with the signup and login
// screens. It used to be an `inviteAcceptErrorMessage` helper here — a third
// byte-identical copy of the same "return err.message for anything" logic,
// which rendered raw `fetch` failures ("Failed to fetch" / "Load failed") as
// red form copy on this screen too. See that module.
